import type { CurrentUser, Site, TenantRole, Vehicle } from "@fts/core";

/** Role the user effectively holds in a tenant. Platform admins act as tenant admins. */
export function effectiveRole(user: CurrentUser, tenantId: string): TenantRole | null {
  const m = user.memberships.find((x) => x.tenantId === tenantId);
  if (user.platformAdmin) return "admin";
  return m?.role ?? null;
}

/**
 * Registration status derived from master data only — no telemetry yet, so we
 * deliberately do not claim online/offline/working states.
 */
export type VehicleStatus = "ready" | "no_device" | "inactive";

export function vehicleStatus(v: Vehicle): VehicleStatus {
  if (!v.active) return "inactive";
  return v.deviceId ? "ready" : "no_device";
}

export const STATUS_TONE: Record<VehicleStatus, "ok" | "warn" | "neutral"> = {
  ready: "ok",
  no_device: "warn",
  inactive: "neutral",
};

/** Ids of the given site and all its descendants. */
export function descendantIds(sites: Site[], rootId: string): Set<string> {
  const out = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of sites) {
      if (s.parentId && out.has(s.parentId) && !out.has(s.id)) {
        out.add(s.id);
        grew = true;
      }
    }
  }
  return out;
}

/** Path from the top-most visible ancestor down to the site (inclusive). */
export function sitePath(sites: Site[], siteId: string): Site[] {
  const byId = new Map(sites.map((s) => [s.id, s]));
  const path: Site[] = [];
  let cur = byId.get(siteId);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return path;
}

export function vehiclesUnder(sites: Site[], vehicles: Vehicle[], siteId: string): Vehicle[] {
  const ids = descendantIds(sites, siteId);
  return vehicles.filter((v) => ids.has(v.siteId));
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length >= 2 ? [parts[0]![0], parts[parts.length - 1]![0]] : [parts[0]?.[0], parts[0]?.[1]];
  return letters.filter(Boolean).join("").toLocaleUpperCase("tr");
}
